import { type ReactNode } from 'react';

import { type MediaQueryTarget } from '@pkg/providers';
import { type UtcMilliseconds } from '@pkg/types';

import { type ResourceId, type TeamLeaderboardItem } from '@app/client';
import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import { type WishlistSummaryResponse } from '@app/client/resources/wishlists/types';
import { type GameItemData, type LeaderboardData } from '@app/pages/FantasyWishlist/sections/LeaderboardSection/types';

import { type ThumbnailProps } from '../Thumbnail';

export enum LeaderboardColumn {
    Audit = 'audit',
    Team = 'team',
    Player = 'player',
    ProfitLoss = 'profitLoss',
    Investment = 'investment',
    CurrentValue = 'currentValue',
    Quantity = 'quantity',
    TopPicks = 'topPicksSlugs',
    Submission = 'submission',
    Status = 'status',
    Chevron = 'chevron',
    StartingValue = 'startingValue',
    TopPerformers = 'topPerformers',
    Rank = 'rank',
    Members = 'members',
}

export enum LeaderboardBadgeVariant {
    Default = 'default',
    Gold = 'gold',
}

// TODO new design differs from the original default x compact - fix this type + sizing
export type LeaderboardBadgeSize = 'default' | 'medium' | 'compact';

export enum LeaderboardDataChipType {
    Number = 'number',
    Date = 'date',
    Usd = 'usd',
}

export enum LeaderboardDataChipVariant {
    Neutral = 'neutral',
    Positive = 'positive',
    Negative = 'negative',
}

export enum LeaderboardDividerVariant {
    Neutral = 'neutral',
    Positive = 'positive',
    Negative = 'negative',
}

export enum LeaderboardPercentVariant {
    Neutral = 'neutral',
    Positive = 'positive',
    Negative = 'negative',
}

export enum LeaderboardRankVariant {
    Default = 'default',
    Gold = 'gold',
    Silver = 'silver',
    Bronze = 'bronze',
    Sexy = 'sexy',
    Last = 'last',
}

export interface LeaderboardBreakpointCondition {
    [MediaQueryTarget.Tablet]: boolean;
    [MediaQueryTarget.Desktop]: boolean;
    [MediaQueryTarget.Widescreen]: boolean;
    [MediaQueryTarget.QuadHdY]: boolean;
}

export type LeaderboardBreakpoints =
    | MediaQueryTarget.Mobile
    | MediaQueryTarget.Tablet
    | MediaQueryTarget.Desktop
    | MediaQueryTarget.Widescreen
    | MediaQueryTarget.QuadHdY;

export interface LeaderboardItemData {
    title: string;
    handle?: string;
    rank?: number;
    maxRank?: number;
    yat?: EmojiId;
    user_id: ResourceId;
    username?: string;
    joined?: UtcMilliseconds;
    submission?: UtcMilliseconds;
    submitted?: boolean;
    profitLoss?: number;
    percentage?: number;
    investment?: number;
    currentValue?: number;
    quantity?: number;
    topPicksSlugs?: string[];
    started?: boolean;
    ended?: boolean;
    winningLineup?: boolean;
    // TODO: How do we determine "season winner" status?
    // If we need to support multiple seasons, then we
    // might need to accept a `number` value?
    seasonWinner?: boolean;
}

export interface LeaderboardProps {
    children: ReactNode;
}

export interface LeaderboardDividerProps {
    variant?: LeaderboardDividerVariant;
}

export interface LeaderboardListProps {
    children: ReactNode;
}

export type LeaderboardItemProps = Partial<TeamLeaderboardItem & GameItemData> &
    LeaderboardData & {
        id: string;
        // children?: ReactNode;
        columns?: LeaderboardColumn[];
        callout?: boolean;
        compact?: boolean;
        loading?: boolean;
        expandedData?: WishlistSummaryResponse;
        isExpanded?: boolean;
        disableHover?: boolean;
        lastRankingMap?: Map<string, number>;
        isTeams?: boolean;
        lineupId?: string;
        onAdminClick?: (item_id?: ResourceId, budget?: number) => void;
    };

export type ThumbsSize = 'small' | 'medium' | 'large';

export interface ThumbsItemProps {
    id?: ThumbnailProps['id'];
    mashWidth?: ThumbnailProps['mashWidth'];
    slug: string;
}

export interface PocketProps {
    count?: number;
}
